import { useCallback, useEffect, useId, useState } from "react";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { toast } from "sonner";
import { giornoScadenza, messaggioErrore } from "@/lib/preventivi";
import { attendiSalvataggi, registraSalvataggio } from "@/lib/salvataggi";
import { Send, Copy, XCircle, CheckCircle2, ExternalLink } from "lucide-react";
import { giorno as giornoLungo } from "@/lib/format";
import { useConferma } from "@/hooks/use-conferma";


/** Selettore del registro (voi | tu) — visibile nella scheda. */
export function RegistroSelector({ preventivoId, valoreAttuale, readOnly, onChanged }) {
  const cambia = async (v) => {
    if (v === valoreAttuale) return;
    try {
      await registraSalvataggio(api.patch(`/preventivatore/${preventivoId}/registro`, { registro: v }));
      onChanged && onChanged();
    } catch (e) { toast.error(messaggioErrore(e)); }
  };
  return (
    <div className="space-y-inside">
      <Label className="text-xs text-muted-foreground">Registro linguistico (documento al cliente)</Label>
      <Select value={valoreAttuale || ""} onValueChange={cambia} disabled={readOnly}>
        <SelectTrigger data-testid="registro-select">
          <SelectValue placeholder="voi oppure tu" />
        </SelectTrigger>
        <SelectContent>
          <SelectItem value="voi" data-testid="registro-voi">Diamo del voi (coppia / famiglia)</SelectItem>
          <SelectItem value="tu"  data-testid="registro-tu">Diamo del tu (persona singola)</SelectItem>
        </SelectContent>
      </Select>
    </div>
  );
}

/** Pulsante «Manda il preventivo» (a inviato: «Rifai il link») + dialog di validazione e di riepilogo link. */
export function PubblicaPreventivoButton({ preventivoId, statoPreventivo, onPublished }) {
  const { chiedi, dialogoConferma } = useConferma();
  const [busy, setBusy] = useState(false);
  const [errori, setErrori] = useState(null);
  const [risultato, setRisultato] = useState(null);
  const [linkEsistente, setLinkEsistente] = useState(null);
  // Due dialoghi fatti a mano, due id: la descrizione era a schermo ma non
  // agganciata via aria-describedby (stesso rimedio di DialogFrame).
  const idErrori = useId();
  const idRisultato = useId();

  /* Il link di un preventivo già inviato si RILEGGE, non si ricrea.
     Prima qui c'era una chiamata che scartava la risposta, con scritto
     «non abbiamo endpoint dedicato»: ora c'è, ed è in sola lettura.
     Serve perché l'unica altra strada era revocare e ripubblicare — cioè
     rompere il link che il cliente ha già in mano per poterlo rileggere. */
  const caricaLink = useCallback(async () => {
    if (statoPreventivo !== "inviato") {
      setLinkEsistente(null);
      return;
    }
    try {
      const r = await api.get(`/preventivatore/${preventivoId}/link`);
      setLinkEsistente(r.data?.esiste ? r.data : null);
    } catch {
      setLinkEsistente(null);
    }
  }, [preventivoId, statoPreventivo]);

  useEffect(() => {
    caricaLink();
  }, [caricaLink]);

  const pubblica = async () => {
    /* Su un preventivo già inviato «Ripubblica» dava sempre 409: il bottone
       non poteva funzionare. Ora funziona, ma il consenso lo si chiede QUI e
       lo si manda al server come `rifai`, in una chiamata sola.
       Non si revoca prima da questa parte: sarebbero due passi, e se il
       secondo cadesse il cliente resterebbe con un indirizzo morto e il
       preventivo senza link. Il server sostituisce il token in un colpo, e
       solo dopo aver validato. */
    if (statoPreventivo === "inviato") {
      const ok = await chiedi({
        titolo: "Rifare il link per il cliente?",
        descrizione:
          "Il link che il cliente ha già ricevuto smette di funzionare: se ci riprova trova una pagina che non esiste. Al suo posto ne nasce uno nuovo, da mandargli.",
        verbo: "Rifai il link",
      });
      if (!ok) return;
    }
    setBusy(true); setErrori(null); setRisultato(null);
    try {
      // Prima si aspetta che arrivi l'ultimo salvataggio: il clic su questo
      // bottone è ciò che fa partire il salvataggio del campo appena scritto,
      // e il controllo che parte insieme a lui lo darebbe per mancante.
      await attendiSalvataggi();
      const v = await api.post(`/preventivatore/${preventivoId}/valida-per-invio`);
      if (!v.data.pronto) {
        setErrori(v.data.errori);
        setBusy(false);
        return;
      }
      const r = await api.post(`/preventivatore/${preventivoId}/pubblica`, {
        rifai: statoPreventivo === "inviato",
      });
      setRisultato(r.data);
      setLinkEsistente({ esiste: true, stato: "attivo", ...r.data });
      onPublished && onPublished();
      toast.success("Preventivo pubblicato. Il link è pronto da inviare al cliente.");
    } catch (e) {
      const d = e.response?.data?.detail;
      if (d && d.errori) {
        setErrori(d.errori);
      } else {
        toast.error(messaggioErrore(e));
      }
    } finally { setBusy(false); }
  };

  /* L'anteprima NON passa dal link del cliente: aprirlo da qui contava come
     un'apertura del cliente, e il preventivo smetteva di risultare «mai
     aperto» proprio quando nessuno l'aveva letto. La si chiede al gestionale,
     che non la registra, e la si apre in una scheda nuova. La scheda si apre
     SUBITO (nel clic), altrimenti il browser la blocca come popup.
     Il documento sta in un iframe sandbox senza script: la scheda nuova ha
     l'origine del gestionale (e la sessione del titolare), il documento no. */
  const apriAnteprima = async () => {
    const finestra = window.open("", "_blank");
    try {
      const r = await api.get(`/preventivatore/${preventivoId}/anteprima`, { responseType: "text" });
      if (!finestra) {
        toast.error("Il browser ha bloccato la scheda: consenti i popup per il gestionale.");
        return;
      }
      const d = finestra.document;
      d.title = "Anteprima del documento";
      const stile = d.createElement("style");
      stile.textContent = "html,body{margin:0;height:100%}iframe{border:0;display:block;width:100%;height:100%}";
      d.head.appendChild(stile);
      const cornice = d.createElement("iframe");
      cornice.setAttribute("sandbox", "allow-popups allow-popups-to-escape-sandbox");
      cornice.srcdoc = r.data;
      d.body.replaceChildren(cornice);
    } catch (e) {
      if (finestra) finestra.close();
      toast.error(messaggioErrore(e));
    }
  };

  const revoca = async () => {
    const ok = await chiedi({
      titolo: "Revocare il link pubblico?",
      descrizione:
        "Il cliente non potrà più aprire il documento: se ci riprova trova una pagina che non esiste. Per rimandarglielo si pubblica di nuovo, con un link diverso.",
      verbo: "Revoca il link",
    });
    if (!ok) return;
    try {
      await registraSalvataggio(api.post(`/preventivatore/${preventivoId}/link/revoca`));
      toast.success("Link revocato");
      setRisultato(null);
      setLinkEsistente(null);
      onPublished && onPublished();
    } catch (e) { toast.error(messaggioErrore(e)); }
  };

  return (
    <>
      {dialogoConferma}
      <Button
        size="sm"
        variant={statoPreventivo === "inviato" ? "outline" : "default"}
        onClick={pubblica}
        disabled={busy}
        data-testid="btn-pubblica"
      >
        <Send /> {statoPreventivo === "inviato" ? "Rifai il link" : "Manda il preventivo"}
      </Button>
      {linkEsistente ? (
        <Button
          size="sm"
          variant="outline"
          onClick={() => setRisultato(linkEsistente)}
          data-testid="btn-vedi-link"
        >
          <ExternalLink /> Vedi il link
        </Button>
      ) : null}
      {statoPreventivo === "inviato" ? (
        <Button size="sm" variant="outline" onClick={revoca} data-testid="btn-revoca-link">
          <XCircle /> Revoca il link
        </Button>
      ) : null}

      {errori ? (
        <div className="fixed inset-0 z-50 grid place-items-center bg-ink-strong/40 p-4">
          <div
            role="dialog"
            aria-modal="true"
            aria-label="Il preventivo non è pronto per l'invio"
            aria-describedby={idErrori}
            className="w-full max-w-lg space-y-4 rounded-xl border border-line bg-surface p-5"
            data-testid="dialog-errori-invio"
          >
            <div className="flex items-start gap-3">
              <XCircle className="mt-0.5 size-6 text-danger" aria-hidden />
              <div>
                <h3 className="font-display text-h3 text-ink-strong">Il preventivo non è pronto per l'invio</h3>
                <p id={idErrori} className="mt-1 text-ink-2">Sistema questi punti prima di mandarlo.</p>
              </div>
            </div>
            <ul className="space-y-2">
              {errori.map((e, i) => (
                <li key={i} className="rounded-md border border-line bg-surface-2 p-3" data-testid={`errore-${e.codice}`}>
                  <p className="text-small font-semibold text-ink-2">Blocco «{e.blocco}»</p>
                  <p className="text-ink">{e.messaggio}</p>
                </li>
              ))}
            </ul>
            <div className="flex justify-end">
              <Button variant="outline" onClick={() => setErrori(null)}>Chiudi</Button>
            </div>
          </div>
        </div>
      ) : null}

      {risultato ? (
        <div className="fixed inset-0 z-50 grid place-items-center bg-ink-strong/40 p-4">
          <div
            role="dialog"
            aria-modal="true"
            aria-label="Manda il preventivo"
            aria-describedby={idRisultato}
            className="w-full max-w-lg space-y-4 rounded-xl border border-line bg-surface p-5"
            data-testid="dialog-link-pubblico"
          >
            <div className="flex items-start gap-3">
              <CheckCircle2 className="mt-0.5 size-6 text-success" aria-hidden />
              <div>
                <h3 className="font-display text-h3 text-ink-strong">
                  {risultato.stato && risultato.stato !== "attivo"
                    ? (risultato.stato === "scaduto" ? "Questo link è scaduto" : "Questo link è stato revocato")
                    : "Il link è pronto"}
                </h3>
                <p id={idRisultato} className="mt-1 text-ink-2">
                  Copialo e mandalo al cliente su WhatsApp o via email. Il documento non contiene JavaScript né chiavi.
                </p>
              </div>
            </div>
            <div className="space-y-inside">
              <Label>Link del cliente</Label>
              <div className="flex gap-2">
                <Input readOnly value={risultato.link_pubblico} data-testid="link-pubblico-input" />
                <Button variant="outline" aria-label="Copia il link" onClick={() => {
                  navigator.clipboard.writeText(risultato.link_pubblico);
                  toast.success("Link copiato");
                }} data-testid="btn-copy-link">
                  <Copy />
                </Button>
              </div>
            </div>
            <p className="text-ink-2">
              {risultato.stato && risultato.stato !== "attivo" ? "Scadenza: " : "Vale fino a "}
              {risultato.scadenza ? giornoLungo(giornoScadenza(risultato.scadenza)) : "—"}.
            </p>
            <div className="flex flex-wrap items-center justify-between gap-3">
              <button
                type="button"
                onClick={apriAnteprima}
                className="inline-flex min-h-11 items-center gap-1 text-ink-2 hover:text-ink-strong hover:underline"
                data-testid="link-anteprima"
              >
                <ExternalLink className="size-4" aria-hidden /> Apri l&#39;anteprima del documento
              </button>
              <Button variant="outline" onClick={() => setRisultato(null)}>Chiudi</Button>
            </div>
          </div>
        </div>
      ) : null}
    </>
  );
}
